import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography, Paper, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import API_BASE_URL from '../config';
import '../pages/admin/AdminDashboard.css';

const COMPONENTS = [
  'Foundation (footing)',
  'Structure - Pole',
  'Structure – Battery Enclosure',
  'Structure – Cabinet',
  'Structure - Fixings',
  'Structure – Locks & Fittings',
  'Structure – Labelling & Signage',
  'Faceplate – Enclosure',
  'Faceplate – LED lights',
  'Electrical Supply – PV panel',
  'Electrical Supply – Wiring',
  'Electrical Supply – Battery',
  'Electrical Supply – Control System'
];

const SCORE_COLORS = {
  1: { bg: '#e8f5e9', fg: '#1b5e20', border: '#a5d6a7' },
  2: { bg: '#f1f8e9', fg: '#33691e', border: '#c5e1a5' },
  3: { bg: '#fff8e1', fg: '#f57f17', border: '#ffe082' },
  4: { bg: '#fff3e0', fg: '#e65100', border: '#ffcc80' },
  5: { bg: '#ffebee', fg: '#b71c1c', border: '#ef9a9a' }
};

const SCORE_GUIDE = [
  {
    score: '1',
    rating: 'Very good (as new)',
    life: '90% – 100%',
    description: 'Free of defects with little or no deterioration evident, (e.g., as new condition, clean free of debris, no corrosion, not covered with vegetation, functioning properly)'
  },
  {
    score: '2',
    rating: 'Good',
    life: '56% – 90%',
    description: 'Deterioration of a minor nature, (e.g. functioning properly, minor cracks)'
  },
  {
    score: '3',
    rating: 'Fair',
    life: '25% – 56%',
    description: 'Moderate to significant deterioration, (e.g., functioning properly, visible cracks, signs of corrosion)'
  },
  {
    score: '4',
    rating: 'Poor',
    life: '6% – 25%',
    description: 'Significant deterioration and defects. (e.g., frequent breakdown, service issues, nearing to fail)'
  },
  {
    score: '5',
    rating: 'Very Poor',
    life: '0% – 6%',
    description: 'Failed or failure imminent, (e.g. completely broken, not operational)'
  }
];

const COMPONENT_COL_TRACK = `minmax(9rem, min(${Math.max(...COMPONENTS.map((name) => name.length)) + 2}ch, 50%))`;

const SectionTitle = ({ step, children }) => (
  <Box
    sx={{
      mt: 3,
      mb: 1.75,
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      '&:first-of-type': { mt: 0.75 }
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '8px',
        background: 'linear-gradient(145deg, #43a047 0%, #2e7d32 100%)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 6px rgba(46, 125, 50, 0.35)'
      }}
    >
      {step}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111', lineHeight: 1.2 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.75, borderColor: 'rgba(46, 125, 50, 0.18)' }} />
    </Box>
  </Box>
);

const TwoCol = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 1.25
    }}
  >
    {children}
  </Box>
);

const formatAuDate = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return 'No date';
  const raw = String(value).trim();
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) return raw;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${parsed.getFullYear()}`;
  }
  return raw;
};

const formatCoords = (lat, lon) => {
  const latitude = lat === undefined || lat === null || lat === '' ? null : Number(lat);
  const longitude = lon === undefined || lon === null || lon === '' ? null : Number(lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

const LocationCell = ({ device }) => {
  const coords = formatCoords(device.latitude, device.longitude);
  if (!coords) {
    return <Typography variant="body2" sx={{ color: '#888' }}>No coordinates</Typography>;
  }
  const openMap = () => {
    const lat = Number(device.latitude);
    const lon = Number(device.longitude);
    window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank', 'noopener,noreferrer');
  };
  return (
    <Tooltip title="Open in Google Maps">
      <Button
        size="small"
        variant="text"
        onClick={openMap}
        sx={{
          color: '#1565c0',
          textTransform: 'none',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          px: 0,
          py: 0.25,
          minHeight: 32,
          width: '100%',
          maxWidth: '100%',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.35,
          whiteSpace: 'normal',
          textAlign: 'left',
          display: 'block'
        }}
      >
        {coords}
      </Button>
    </Tooltip>
  );
};

const ReportsList = ({ device, reports, onView, onDownload }) => {
  const items = reports || [];
  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#888', py: 0.5 }}>
        No reports yet
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: 168,
        overflowY: 'auto',
        border: '1px solid #d0d0d0',
        borderRadius: 1,
        backgroundColor: '#fff',
        minWidth: 0
      }}
    >
      {items.map((report, index) => (
        <Box
          key={report.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderBottom: index === items.length - 1 ? 'none' : '1px solid #ececec',
            minHeight: 36
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#222',
                fontSize: 12,
                lineHeight: 1.3,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
              title={`${formatAuDate(report.inspection_date)} · ${report.inspector_name || 'No inspector'}`}
            >
              {formatAuDate(report.inspection_date)}
              <Box component="span" sx={{ color: '#777' }}>
                {' · '}{report.inspector_name || 'No inspector'}
              </Box>
            </Typography>
          </Box>
          <Button size="small" onClick={() => onView(device, report.id)} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, flexShrink: 0 }}>
            View
          </Button>
          <Button size="small" onClick={() => onDownload(device, report.id, 'docx')} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, flexShrink: 0 }}>
            Word
          </Button>
          <Button size="small" onClick={() => onDownload(device, report.id, 'pdf')} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, flexShrink: 0 }}>
            PDF
          </Button>
        </Box>
      ))}
    </Box>
  );
};

const DetailField = ({ label, value }) => (
  <Box
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: '8px',
      backgroundColor: 'rgba(255,255,255,0.9)',
      border: '1px solid rgba(46, 125, 50, 0.12)',
      minHeight: 52
    }}
  >
    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.25, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: '#111', wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const UserInspection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const useCardLayout = useMediaQuery(theme.breakpoints.down('lg'));
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDeviceName, setPreviewDeviceName] = useState('');
  const [previewDateLabel, setPreviewDateLabel] = useState('');
  const [previewReport, setPreviewReport] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchDevices = async () => {
    const response = await fetch(`${API_BASE_URL}/users/inspection-devices`, {
      headers: authHeaders()
    });
    if (response.status === 401) {
      navigate('/inspection-signin');
      return;
    }
    const data = await response.json();
    setDevices(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchDevices().catch((error) => {
      console.error(error);
      setMessage('Failed to load inspection devices');
    });
  }, []);

  const visibleDevices = useMemo(() => {
    if (!search.trim()) return devices;
    const query = search.toLowerCase().trim();
    return devices.filter((device) => {
      const fields = [
        device.device_name,
        device.description,
        device.street_address,
        device.suburb,
        device.longitude,
        device.latitude,
        device.created_at,
        ...(device.reports || []).flatMap((report) => [
          report.inspector_name,
          report.inspection_date,
          report.updated_at,
          report.created_at
        ])
      ];
      return fields
        .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
        .map((value) => String(value).toLowerCase())
        .join(' ')
        .includes(query);
    });
  }, [devices, search]);

  const openPreview = async (device, reportId) => {
    setMessage('');
    const response = await fetch(`${API_BASE_URL}/users/inspection-reports/${reportId}`, {
      headers: authHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
      setMessage(data.message || 'Failed to open report');
      return;
    }
    setPreviewDeviceName(device.device_name || 'Device');
    setPreviewDateLabel(formatAuDate(data.report_data?.inspectionDate));
    setPreviewReport(data.report_data || {});
    setPreviewOpen(true);
  };

  const downloadReport = async (device, reportId, format) => {
    setMessage('');
    const response = await fetch(
      `${API_BASE_URL}/users/inspection-reports/${reportId}/download?format=${format}`,
      { headers: authHeaders() }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      setMessage(data.message || 'Failed to download report');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${device.device_name}_Condition_Inspection.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const report = previewReport || {};
  const thumbSize = isMobile ? 112 : 140;

  const assetFields = [
    ['Asset number', report.assetNumber],
    ['Inspector\'s name', report.inspectorName],
    ['Asset description', report.assetDescription],
    ['Asset location', report.assetLocation],
    ['Inspection date', formatAuDate(report.inspectionDate)],
    ['Make', report.make],
    ['Model', report.model],
    ['Serial number', report.serialNumber],
    ['Manufacture year', report.manufactureYear],
    ['Useful life', report.usefulLife],
    ['Remaining useful life', report.remainingUsefulLife],
    ['Estimated replacement cost', report.estimatedReplacementCost],
    ['Sign pole material', report.signPoleMaterial],
    ['Sign pole height', report.signPoleHeight],
    ['Sign pole diameter', report.signPoleDiameter],
    ['Sign pole type', report.signPoleType],
    ['MUTCD sign code', report.mutcdSignCode],
    ['Sign text', report.signText],
    ['Zone type', report.zoneType],
    ['Battery make and model', report.batteryMakeModel],
    ['Battery capacity', report.batteryCapacity],
    ['PV panel no. of cells', report.pvCellCount],
    ['PV panel capacity', report.pvCapacity],
    ['Grid-connect supply', report.gridConnect]
  ];

  return (
    <div className="admin-dashboard" style={{ backgroundColor: 'transparent', minHeight: 'auto', padding: 0 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ flexGrow: 1, color: '#111' }}>
          My Inspection Devices
        </Typography>
        <TextField
          size={isMobile ? 'medium' : 'small'}
          label="Search"
          placeholder="Device, address, inspector, date..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          fullWidth={useCardLayout}
          sx={{ minWidth: { lg: 280 }, maxWidth: { lg: 360 }, backgroundColor: '#fff' }}
        />
      </Stack>
      {message && (
        <Typography sx={{ mb: 1, color: message.includes('Failed') ? '#c62828' : '#2e7d32' }}>
          {message}
        </Typography>
      )}

      {useCardLayout ? (
        <Stack spacing={1.5} sx={{ pb: 3 }}>
          {visibleDevices.map((device) => (
            <Card key={device.id} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box sx={{ minWidth: 0, mb: 1.25 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111', mb: 0.25 }}>
                    {device.device_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    {[device.description, device.street_address, device.suburb].filter(Boolean).join(' · ') || 'No location'}
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <LocationCell device={device} />
                  </Box>
                </Box>
                <ReportsList
                  device={device}
                  reports={device.reports}
                  onView={openPreview}
                  onDownload={downloadReport}
                />
              </CardContent>
            </Card>
          ))}
          {visibleDevices.length === 0 && (
            <Paper sx={{ p: 2, backgroundColor: '#eee' }}>
              <Typography>No inspection devices found for this account.</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#cacaca', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 980, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 130 }}>Device ID</TableCell>
                <TableCell sx={{ width: 180 }}>Description</TableCell>
                <TableCell sx={{ width: 220 }}>Location</TableCell>
                <TableCell sx={{ width: 170 }}>Coordinates</TableCell>
                <TableCell sx={{ width: 300 }}>Reports</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDevices.map((device, index) => (
                <TableRow key={device.id} sx={{ backgroundColor: index % 2 ? '#e6e6e6' : '#f5f5f5', verticalAlign: 'top' }}>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{device.device_name}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{device.description}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {[device.street_address, device.suburb].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    <LocationCell device={device} />
                  </TableCell>
                  <TableCell>
                    <ReportsList
                      device={device}
                      reports={device.reports}
                      onView={openPreview}
                      onDownload={downloadReport}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {visibleDevices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>No inspection devices found for this account.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        scroll="paper"
        BackdropProps={{
          sx: { backgroundColor: 'rgba(15, 23, 15, 0.45)', backdropFilter: 'blur(2px)' }
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#fafbfa',
            color: '#111',
            height: { xs: '100%', sm: 'min(92vh, 920px)' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: '16px' },
            overflow: 'hidden',
            boxShadow: {
              xs: 'none',
              sm: '0 24px 64px rgba(20, 40, 20, 0.22), 0 0 0 1px rgba(46, 125, 50, 0.08)'
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 55%, #43a047 100%)',
            color: '#fff',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              background: 'linear-gradient(90deg, #a5d6a7, #fff59d, #a5d6a7)'
            }
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 1.2, lineHeight: 1, display: 'block', mb: 0.5 }}
            >
              Condition inspection
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: '#fff' }}>
              View Report
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)', mt: 0.4, fontWeight: 500 }}>
              {previewDeviceName}
              {previewDateLabel ? ` · ${previewDateLabel}` : ''}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={() => setPreviewOpen(false)}
            size="small"
            sx={{
              mt: -0.25,
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            background: 'linear-gradient(180deg, #f7faf7 0%, #ffffff 120px)',
            color: '#111',
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderColor: 'rgba(46, 125, 50, 0.12)'
          }}
        >
          <SectionTitle step={1}>Asset details</SectionTitle>
          <TwoCol>
            {assetFields.map(([label, value]) => (
              <DetailField key={label} label={label} value={value} />
            ))}
          </TwoCol>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1b5e20',
              mt: 2.5,
              mb: 1,
              borderLeft: '3px solid #66bb6a',
              pl: 1.25
            }}
          >
            Asset photographs
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
            {(report.assetPhotos || []).map((photo) => (
              <Box key={photo.name} sx={{ width: thumbSize }}>
                <img
                  src={photo.dataUrl}
                  alt={photo.name}
                  onClick={() => setPreviewImage(photo)}
                  style={{
                    width: thumbSize,
                    height: Math.round(thumbSize * 0.78),
                    objectFit: 'cover',
                    cursor: 'pointer',
                    border: '1px solid #ccc',
                    borderRadius: 8
                  }}
                />
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{photo.name}</Typography>
              </Box>
            ))}
            {!(report.assetPhotos || []).length && <Typography variant="body2">No photos</Typography>}
          </Stack>

          <SectionTitle step={2}>Component scoring</SectionTitle>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mb: 2.5,
              border: '1px solid rgba(46, 125, 50, 0.2)',
              borderRadius: '12px !important',
              backgroundColor: '#fff',
              boxShadow: '0 4px 18px rgba(20, 40, 20, 0.06)',
              overflow: 'hidden',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                background: 'linear-gradient(90deg, rgba(232,245,233,0.9), #fff)',
                '& .MuiAccordionSummary-content': { my: 1.25 }
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1b5e20' }}>
                  Score guide (Appendix E)
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Condition score, rating, remaining useful life, and description
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: { xs: 1, sm: 2 }, pb: 2 }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: { xs: 520, sm: 640 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 64 }}>CS</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 140 }}>Subjective rating</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 110 }}>Remaining life</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {SCORE_GUIDE.map((row) => {
                      const colors = SCORE_COLORS[row.score] || SCORE_COLORS[3];
                      return (
                        <TableRow
                          key={row.score}
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.015)' },
                            verticalAlign: 'top'
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: 14,
                                color: colors.fg,
                                backgroundColor: colors.bg,
                                border: `1px solid ${colors.border}`
                              }}
                            >
                              {row.score}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#222' }}>{row.rating}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#444' }}>{row.life}</TableCell>
                          <TableCell sx={{ color: '#444', fontSize: 13, lineHeight: 1.45 }}>
                            {row.description}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Box
            sx={{
              border: '1px solid rgba(46, 125, 50, 0.14)',
              borderRadius: '12px',
              backgroundColor: '#fff',
              boxShadow: '0 4px 18px rgba(20, 40, 20, 0.05)',
              px: { xs: 1.25, sm: 2 },
              py: 1.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: `${COMPONENT_COL_TRACK} 72px minmax(0, 1fr)`
              },
              columnGap: 1.25,
              rowGap: 1.25,
              alignItems: 'start'
            }}
          >
            {COMPONENTS.map((name, index) => {
              const component = (report.components || []).find((item) => item.name === name) || {};
              const score = component.conditionScore || '—';
              const colors = SCORE_COLORS[score] || null;
              return (
                <Box
                  key={name}
                  sx={{
                    display: 'contents',
                    '@media (max-width: 599.95px)': {
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      columnGap: 1,
                      rowGap: 0.5,
                      pb: index < COMPONENTS.length - 1 ? 1.25 : 0,
                      mb: index < COMPONENTS.length - 1 ? 0.25 : 0,
                      borderBottom: index < COMPONENTS.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#222',
                      pt: 0.5,
                      gridColumn: { xs: '1 / 2', sm: 'auto' }
                    }}
                  >
                    {name}
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 14,
                      color: colors ? colors.fg : '#555',
                      backgroundColor: colors ? colors.bg : '#f5f5f5',
                      border: `1px solid ${colors ? colors.border : '#ddd'}`,
                      justifySelf: { xs: 'end', sm: 'start' }
                    }}
                  >
                    {score}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#444',
                      pt: 0.65,
                      gridColumn: { xs: '1 / -1', sm: 'auto' }
                    }}
                  >
                    {component.comments || '—'}
                  </Typography>
                  {index < COMPONENTS.length - 1 && (
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        borderBottom: '1px solid #f0f0f0',
                        display: { xs: 'none', sm: 'block' }
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          <SectionTitle step={3}>Defects</SectionTitle>
          {!(report.defects || []).length && (
            <Typography variant="body2" sx={{ color: '#555' }}>No defects recorded</Typography>
          )}
          {(report.defects || []).map((defect, index) => (
            <Accordion
              key={index}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.5,
                border: '1px solid rgba(46, 125, 50, 0.18)',
                borderRadius: '12px !important',
                backgroundColor: '#fff',
                boxShadow: '0 3px 12px rgba(20, 40, 20, 0.05)',
                overflow: 'hidden',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  {defect.defectNumber || `Defect ${index + 1}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TwoCol>
                  {[
                    ['Inspector\'s name', defect.inspectorName],
                    ['Inspection date', formatAuDate(defect.inspectionDate)],
                    ['Defect number', defect.defectNumber],
                    ['Area', defect.area],
                    ['Type code', defect.typeCode],
                    ['Severity', defect.severity],
                    ['Details', defect.details],
                    ['Recommendation', defect.recommendation],
                    ['Repair cost', defect.cost]
                  ].map(([label, value]) => (
                    <DetailField key={label} label={label} value={value} />
                  ))}
                </TwoCol>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1.5 }}>
                  {(defect.photos || []).map((photo) => (
                    <Box key={photo.name} sx={{ width: thumbSize }}>
                      <img
                        src={photo.dataUrl}
                        alt={photo.name}
                        onClick={() => setPreviewImage(photo)}
                        style={{
                          width: thumbSize,
                          height: Math.round(thumbSize * 0.78),
                          objectFit: 'cover',
                          cursor: 'pointer',
                          border: '1px solid #ccc',
                          borderRadius: 8
                        }}
                      />
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{photo.name}</Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>

        <DialogActions
          sx={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f3f7f3 100%)',
            px: { xs: 2, sm: 3 },
            py: 1.85,
            borderTop: '1px solid rgba(46, 125, 50, 0.14)'
          }}
        >
          <Button
            fullWidth={isMobile}
            onClick={() => setPreviewOpen(false)}
            sx={{
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 600,
              color: '#333',
              border: '1px solid #d0d0d0',
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bbb' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} fullScreen={isMobile} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#111' }}>{previewImage?.name}</DialogTitle>
        <DialogContent sx={{ backgroundColor: '#111', textAlign: 'center' }}>
          {previewImage && (
            <img src={previewImage.dataUrl} alt={previewImage.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button fullWidth={isMobile} onClick={() => setPreviewImage(null)} sx={{ minHeight: 48 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserInspection;
